import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useAppDispatch } from "../../common/state/hooks";
import { IsLoading } from "../../common/components/AppState/IsLoading";
import {
  fetchEnduserStatsOverview,
  fetchProjectGroupsCompletion,
  fetchEnduserAllSessions,
  getEnduserProjectGroupsCompletion,
  getEnduserAllSessions,
} from "../../features/enduserStats/state/enduserStatsSlice";
import { useEnduserStatsCache } from "../../features/enduserStats/hooks";
import { StatsCard, EmptyState } from "../../features/statsNew/components";
import EnduserActivityHeatmap from "../../features/enduserStats/components/EnduserActivityHeatmap";
import EnduserSessionsPerMonthChart from "../../features/enduserStats/components/EnduserSessionsPerMonthChart";
import EnduserFunnelChart from "../../features/enduserStats/components/EnduserFunnelChart";
import { DateRange } from "../../features/statsNew/components/DateRangePicker";

const ENDUSER_STATS_OVERVIEW_COMPONENT_ID = "enduserStatsOverviewPage";

interface OutletContextType {
  dateRange: DateRange;
  clearDateRange: () => void;
}

const EnduserStatsOverviewPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { dateRange, clearDateRange } = useOutletContext<OutletContextType>();
  const locale = i18n.language === "fr" ? fr : enUS;
  const { shouldRefetch, markAsFetched } = useEnduserStatsCache();

  const projectGroupsCompletion = useSelector(getEnduserProjectGroupsCompletion);
  const allSessions = useSelector(getEnduserAllSessions);

  // Fetch only if cache is invalid (user changed or first load)
  useEffect(() => {
    if (shouldRefetch) {
      Promise.all([
        dispatch(fetchEnduserStatsOverview({ componentId: ENDUSER_STATS_OVERVIEW_COMPONENT_ID })),
        dispatch(
          fetchProjectGroupsCompletion({
            componentId: ENDUSER_STATS_OVERVIEW_COMPONENT_ID,
            limit: -1,
          }),
        ),
        dispatch(fetchEnduserAllSessions({ componentId: ENDUSER_STATS_OVERVIEW_COMPONENT_ID })),
      ]).then(() => {
        markAsFetched();
      });
    }
  }, [dispatch, shouldRefetch, markAsFetched]);

  // Calculate stats based on date range
  const stats = useMemo(() => {
    let totalSessions = 0;
    let lastSessionDate: Date | null = null;

    // Filter sessions by date range
    allSessions.forEach((session) => {
      const sessionDate = new Date(session.createdAt);

      // Apply date range filter
      if (dateRange.startDate && sessionDate < dateRange.startDate) return;
      if (dateRange.endDate && sessionDate > dateRange.endDate) return;

      totalSessions++;

      // Track most recent session (without date filter for last session display)
      if (!lastSessionDate || sessionDate > lastSessionDate) {
        lastSessionDate = sessionDate;
      }
    });

    // If no sessions in range, get the actual last session date from all sessions
    if (!lastSessionDate && allSessions.length > 0) {
      const sortedSessions = [...allSessions].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      lastSessionDate = new Date(sortedSessions[0].createdAt);
    }

    // Find last parcours info
    let lastParcoursName = "-";
    let lastParcoursId: number | null = null;
    let lastParcoursSessionCount = 0;
    let completedCount = 0;
    let totalCount = 0;

    if (projectGroupsCompletion.length > 0) {
      totalCount = projectGroupsCompletion.length;

      // Sort by latest session date
      const sorted = [...projectGroupsCompletion].sort((a, b) => {
        const aDate = a.dateLastSession ? new Date(a.dateLastSession).getTime() : 0;
        const bDate = b.dateLastSession ? new Date(b.dateLastSession).getTime() : 0;
        return bDate - aDate;
      });

      if (sorted[0]) {
        lastParcoursName = sorted[0].projectGroupName || "-";
        lastParcoursId = sorted[0].projectGroupId;
      }

      // Count completed parcours
      completedCount = projectGroupsCompletion.filter(
        (pg) => pg.progressPercentageOnLatestBuild === 100,
      ).length;
    }

    // Count sessions for the last parcours (filtered by date range)
    if (lastParcoursId) {
      allSessions.forEach((session) => {
        if (session.projectGroupId !== lastParcoursId) return;

        const sessionDate = new Date(session.createdAt);
        if (dateRange.startDate && sessionDate < dateRange.startDate) return;
        if (dateRange.endDate && sessionDate > dateRange.endDate) return;

        lastParcoursSessionCount++;
      });
    }

    // Find last session info for linking
    let lastSessionId: number | null = null;
    let lastSessionParcoursId: number | null = null;
    let lastSessionModuleId: number | null = null;
    let lastModuleName: string | null = null;
    let lastModuleAvgDuration: number | null = null;
    if (allSessions.length > 0) {
      const sortedSessions = [...allSessions].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      const lastSession = sortedSessions[0];
      lastSessionId = lastSession.id;
      lastSessionParcoursId = lastSession.projectGroupId;
      lastSessionModuleId = lastSession.projectId;
      lastModuleName = lastSession.projectName;

      // Calculate average duration for sessions on this module
      if (lastSessionModuleId) {
        const moduleSessions = allSessions.filter(
          (s) => s.projectId === lastSessionModuleId && s.duration != null,
        );
        if (moduleSessions.length > 0) {
          const totalDuration = moduleSessions.reduce((acc, s) => acc + (s.duration ?? 0), 0);
          lastModuleAvgDuration = totalDuration / moduleSessions.length;
        }
      }
    }

    return {
      totalSessions,
      lastParcoursName,
      lastParcoursId,
      lastParcoursSessionCount,
      completedCount,
      totalCount,
      lastSessionDate,
      lastSessionId,
      lastSessionParcoursId,
      lastSessionModuleId,
      lastModuleName,
      lastModuleAvgDuration,
    };
  }, [allSessions, projectGroupsCompletion, dateRange]);

  // Check if there are any sessions without date filter
  const hasAnySessionsWithoutFilter = allSessions.length > 0;

  // Check if date filter is active
  const hasDateFilter = dateRange.startDate !== null || dateRange.endDate !== null;

  const formatLastSessionDate = () => {
    if (!stats.lastSessionDate) return "-";
    return format(stats.lastSessionDate, "dd MMM yyyy", { locale });
  };

  const formatTimeAgo = () => {
    if (!stats.lastSessionDate) return null;
    return formatDistanceToNow(stats.lastSessionDate, { addSuffix: true, locale });
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    if (mins < 1) return "< 1 min";
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return remainingMins > 0 ? `${hours}h ${remainingMins}min` : `${hours}h`;
  };

  // Show empty state if no sessions for date range but sessions exist without filter
  if (stats.totalSessions === 0 && hasDateFilter && hasAnySessionsWithoutFilter) {
    return (
      <EmptyState
        icon="calendar"
        title={t("statsNew.emptyState.noData")}
        description={t("statsNew.emptyState.noDataDescription")}
        action={{
          label: t("statsNew.emptyState.viewAll"),
          onClick: clearDateRange,
        }}
      />
    );
  }

  return (
    <IsLoading
      componentId={ENDUSER_STATS_OVERVIEW_COMPONENT_ID}
      showSuccess={false}
      spinnerPlaceholder
    >
      <div className="space-y-3 overflow-y-auto h-full">
        {/* Row 1: KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatsCard
            title={t("statsNew.global.totalSessions")}
            value={stats.totalSessions.toString()}
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            }
          />
          <StatsCard
            title={t("statsNew.global.lastParcours")}
            value={stats.lastParcoursName}
            href={stats.lastParcoursId ? `/stats/parcours/${stats.lastParcoursId}` : undefined}
            hoverContent={
              stats.lastParcoursSessionCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  {t("statsNew.global.sessionsOnParcours", {
                    count: stats.lastParcoursSessionCount,
                  })}
                </span>
              )
            }
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            }
          />
          <StatsCard
            title={t("enduserStats.heroCards.lastModule")}
            value={stats.lastModuleName || "-"}
            href={
              stats.lastSessionModuleId && stats.lastSessionParcoursId
                ? `/stats/parcours/${stats.lastSessionParcoursId}/${stats.lastSessionModuleId}`
                : undefined
            }
            hoverContent={
              stats.lastModuleAvgDuration != null && (
                <span className="flex items-center gap-1.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {t("statsNew.global.avgDuration", {
                    duration: formatDuration(stats.lastModuleAvgDuration),
                  })}
                </span>
              )
            }
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            }
          />
          <StatsCard
            title={t("statsNew.global.lastSessionDate")}
            value={formatLastSessionDate()}
            href={
              stats.lastSessionId && stats.lastSessionParcoursId && stats.lastSessionModuleId
                ? `/stats/parcours/${stats.lastSessionParcoursId}/${stats.lastSessionModuleId}?sessionId=${stats.lastSessionId}`
                : undefined
            }
            hoverContent={
              stats.lastSessionDate && (
                <span className="flex items-center gap-1.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {formatTimeAgo()}
                </span>
              )
            }
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            }
          />
        </div>

        {/* Row 2: Activity Heatmap */}
        <div className="stats-chart-card w-full">
          <EnduserActivityHeatmap maxCellSize={24} />
        </div>

        {/* Row 3: Charts */}
        <div className="stats-charts-row">
          {/* Sessions per month chart */}
          <div className="stats-chart-card flex flex-col global-chart-container">
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex-shrink-0">
              {t("statsNew.charts.sessionsPerMonth")}
            </h3>
            <div className="flex-1 min-h-0">
              <EnduserSessionsPerMonthChart dateRange={dateRange} />
            </div>
          </div>

          {/* Progression funnel */}
          <div className="stats-chart-card flex flex-col global-chart-container">
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex-shrink-0">
              {t("enduserStats.funnel.title")}
            </h3>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <EnduserFunnelChart />
            </div>
          </div>
        </div>
      </div>
    </IsLoading>
  );
};

export default EnduserStatsOverviewPage;
